/**
 * Canonical error codes returned by the API (see the "Error Handling" section).
 * Every API error response uses the same envelope:
 *   { "error": { "code", "message", "requestId" } }
 */
export const ERROR_CODES = [
  'UNAUTHORIZED',
  'FORBIDDEN',
  'FILE_NOT_FOUND',
  'CONFLICT',
  'PAYLOAD_TOO_LARGE',
  'QUOTA_EXCEEDED',
  'VALIDATION_ERROR',
  'RATE_LIMITED',
  'UPLOAD_STORAGE_ERROR',
  'UPLOAD_INTERRUPTED',
  'INSUFFICIENT_STORAGE',
  'READ_ERROR',
  'WRITE_ERROR',
  'INTERNAL_ERROR',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/** HTTP status paired with each code. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  FILE_NOT_FOUND: 404,
  CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  QUOTA_EXCEEDED: 413,
  VALIDATION_ERROR: 422,
  RATE_LIMITED: 429,
  UPLOAD_STORAGE_ERROR: 500,
  UPLOAD_INTERRUPTED: 500,
  INSUFFICIENT_STORAGE: 507,
  READ_ERROR: 500,
  WRITE_ERROR: 500,
  INTERNAL_ERROR: 500,
};

/**
 * Generic, client-safe messages. These never leak disk paths or raw OS errors —
 * the technical `detail` goes to the transfer log only (see the "Error Handling" and
 * "Transfer history: one table, read from both sides" sections).
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  UNAUTHORIZED: 'Missing or invalid API key.',
  FORBIDDEN: 'This client may only act on its own folder.',
  FILE_NOT_FOUND: 'No file with this id is visible to this client.',
  CONFLICT: 'This idempotency key was already used for a different upload.',
  PAYLOAD_TOO_LARGE: 'File exceeds the maximum upload size.',
  QUOTA_EXCEEDED: 'Storage quota exceeded for this client.',
  VALIDATION_ERROR: 'The request could not be processed as sent.',
  RATE_LIMITED: 'Too many requests. Please slow down and retry.',
  UPLOAD_STORAGE_ERROR: 'The file could not be stored. Please retry.',
  UPLOAD_INTERRUPTED: 'The upload did not complete. Please retry.',
  INSUFFICIENT_STORAGE: 'The server is out of storage space. Please retry later.',
  READ_ERROR: 'The file could not be read. Please retry.',
  WRITE_ERROR: 'The transfer did not complete. Please retry.',
  INTERNAL_ERROR: 'An unexpected error occurred.',
};

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    requestId: string;
  };
}

/**
 * Which codes are the caller's fault and which are ours.
 *
 * Every code in the client-facing table of the "Error Handling" section is here; everything else is a server
 * fault. Shared rather than defined per side, because both the dashboard's labels
 * (`error (client)` / `error (server)`) and the summary strip's error breakdown have to
 * agree about which is which — a split that disagreed with itself would be worse than no
 * split at all.
 */
export const CLIENT_FAULT_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'FILE_NOT_FOUND',
  'CONFLICT',
  'PAYLOAD_TOO_LARGE',
  'QUOTA_EXCEEDED',
  'VALIDATION_ERROR',
  'RATE_LIMITED',
]);

/** True when the caller can fix the problem themselves. */
export function isClientFault(code: string | null | undefined): boolean {
  return code !== null && code !== undefined && CLIENT_FAULT_CODES.has(code as ErrorCode);
}
